import React, { Fragment, useCallback, useContext, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { Col, useDeviceType } from '@cian/ui-kit';

import { TagScrollingContainer } from '../TagScrolling';
import { MainArticleAndMainNewsContainer } from '../MainArticleAndMainNews';
import { selectInitialPageNumber } from '../../selectors/settings/selectInitialPageNumber';
import { IApplicationState, TThunkDispatch } from '../../types/redux';
import { ApplicationContext, preparePageNumber, useGetListing, useListingAnalytics, buildPostUrl } from '../../utils';
import { selectMainArticles } from '../../selectors/mainArticles';
import { Banner } from '../Banner';
import { SubscribeFormContainer } from '../SubscribeForm';
import { CardListingItem } from '../../components/ListingItem';
import { PaginationContainer } from '../Pagination';
import { PR_RANDOM } from '../../components/adfox/constants';
import { getMainArticles } from '../../actions/mainArticles';
import { MAIN_ARTICLES_REQUEST_LIMIT } from '../../constants';
import { selectListingAppendType } from '../../selectors/settings/selectListingAppendType';
import { MainPageSliderContainer } from '../MainPageSlider';
import { EType } from '../../repositories/journal/entities/journal/JournalListAttributesSchema';
import { selectSlider } from '../../selectors/sliders';
import * as s from './Listings.css';

export const MainPage = () => {
  const initialPageNumber = useSelector(selectInitialPageNumber);
  const { search } = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const qsPage = preparePageNumber(searchParams.get('page') as string);
  const qsWithTag = searchParams.get('with-tag') || '';
  const dispatch = useDispatch<TThunkDispatch>();
  const history = useHistory();
  const deviceType = useDeviceType();
  const isPhone = () => deviceType === 'phone';
  const articles = useSelector(selectMainArticles);
  const appendType = useSelector(selectListingAppendType);
  const {
    custom: { mainPageSliderName = '' },
  } = useContext(ApplicationContext);
  const { items: mainPageSliderItems } = useSelector((state: IApplicationState) =>
    selectSlider(state, mainPageSliderName),
  );
  const adfoxParams = {
    p1: 'cxytw',
    p2: 'gqui',
    puid15: 'content_part',
    pr: PR_RANDOM,
    pct: 'a',
    pfc: 'a',
    pfb: 'a',
    plp: 'a',
    pli: 'a',
    pop: 'a',
    pt: 'b',
  };

  useListingAnalytics();

  useGetListing(() => {
    dispatch(
      getMainArticles({
        tag: qsWithTag,
        offset: (qsPage - 1) * MAIN_ARTICLES_REQUEST_LIMIT,
        limit: MAIN_ARTICLES_REQUEST_LIMIT,
        setType: appendType,
      }),
    ).finally();
  });

  /**
   * При клике по плашке статьи роутит на соответствующую страницу статьи
   * При клике по шильдику рубрики в плашке статьи роутит на соответствующую рубрику
   */
  const handleClick = useCallback(
    (e, url: string) => {
      history.push(url);
    },
    [history],
  );

  if (!articles?.length) {
    return null;
  }

  return (
    <>
      <div className={s['tag-scrolling-wrapper']}>
        <TagScrollingContainer />
      </div>

      {/* Показываем только тогда, когда приложение стартовало с первой страницы */}
      {initialPageNumber === 1 && <MainArticleAndMainNewsContainer />}

      <div className={s['cards-wrapper']} data-web-ui-test-id="MainPageListing">
        {articles.map(
          (
            { id, attributes: { image, title, subtitle, datePublish, numViews, commentsCount, rubrics, slug } },
            index,
          ) => (
            <Fragment key={`list-item-${index}`}>
              {/* Баннер и слайдер только для первой страницы */}
              {initialPageNumber === 1 && index === 5 && (
                <>
                  <Col xs={12} m={6} l={4}>
                    <Banner place={'mainpage'} constants={adfoxParams} height={'100%'} className={'wrapper-100'} />
                  </Col>

                  <div className={`${s['insert-wrapper']} ${mainPageSliderItems.length ? s['_active'] : ''}`}>
                    <MainPageSliderContainer />
                  </div>
                </>
              )}

              {/* Форма подписки только для первой страницы */}
              {initialPageNumber === 1 && index === 11 && (
                <div className={`${s['insert-wrapper']} ${s['_active']}`}>
                  <SubscribeFormContainer />
                </div>
              )}

              {/* Статья */}
              <Col xs={12} m={6} l={4}>
                <CardListingItem
                  title={title}
                  subtitle={image ? undefined : subtitle}
                  height={isPhone() ? undefined : 400}
                  image={image}
                  rubric={rubrics?.length ? rubrics[0] : undefined}
                  datePublish={datePublish}
                  numViews={numViews}
                  numComments={commentsCount}
                  url={buildPostUrl({ type: EType.Articles, slug, id })}
                  onRubricButtonClick={handleClick}
                  onClick={e => handleClick(e, buildPostUrl({ type: EType.Articles, slug, id }))}
                />
              </Col>

              {/* Форма подписки, если приложение инициализировано НЕ на первой странице */}
              {initialPageNumber !== 1 && index === 5 && (
                <div className={`${s['insert-wrapper']} ${s['_active']}`}>
                  <SubscribeFormContainer />
                </div>
              )}
            </Fragment>
          ),
        )}
      </div>

      <div className={s['controls-wrapper']}>
        <PaginationContainer />
      </div>
    </>
  );
};
